import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Spacing16 } from 'autogen/design-tokens/tokens';
import type { NavigationButtonProps } from 'components/NavigationButton/NavigationButton';
import type { ScrollSnapSlider } from 'scroll-snap-slider';
import { breakpoint, zIndex } from 'variables';
import useScrollSnapSliderContext from '../hooks/useScrollSnapSliderContext';
import NavigationButton from './NavigationButton';

export type INavigationButton = NavigationButtonProps & {
    slideAmount?: number;
    indent?: string;
};

const StyledButton = styled(NavigationButton)<{ $show: boolean; $indent: string }>`
    && {
        display: none;

        ${breakpoint.up('768px')} {
            display: flex;
            position: absolute;
            top: 50%;
            left: ${({ direction }) => (direction === 'prev' ? 0 : 'initial')};
            right: ${({ direction }) => (direction === 'next' ? 0 : 'initial')};
            opacity: 0;
            transform: translate(0, -50%);
            z-index: ${zIndex.fixed};
            pointer-events: ${({ $show: show }) => (show ? 'auto' : 'none')};
            transition: opacity, transform;
            transition-duration: 300ms;
            transition-timing-function: ease;
            
            ${({ $show, direction, theme }) =>
                $show &&
                css`
                    @media (pointer: fine) {
                        :has(> ${StyledButton}):hover & {
                            opacity: ${$show ? 1 : 0};
                            transform: translate(${direction === 'prev' ? theme.Spacing20 : `-${theme.Spacing20}`}, -50%);
                        }
                    }
                `}

        }
    }
`;

function useToggleNavigationButtons(slider?: ScrollSnapSlider) {
    const [showPrev, setShowPrev] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const observerRef = useRef<IntersectionObserver>();

    useEffect(() => {
        const rootElement = slider?.element;
        const firstSlide = slider?.element.firstElementChild;
        const lastSlide = slider?.element.lastElementChild;

        const callback: IntersectionObserverCallback = (entries) => {
            entries.forEach((entry) => {
                const { target, isIntersecting } = entry;

                if (target === firstSlide) {
                    setShowPrev(!isIntersecting);
                }

                if (target === lastSlide) {
                    setShowNext(!isIntersecting);
                }
            });
        };

        observerRef.current = new IntersectionObserver(callback, { root: rootElement, threshold: 0.8 });

        if (firstSlide && lastSlide) {
            observerRef.current.observe(firstSlide);
            observerRef.current.observe(lastSlide);
        }

        return () => {
            observerRef.current?.disconnect();
        };
    }, [slider]);

    return {
        showPrev,
        showNext
    };
}

export default function NavigationFadeButton({ direction, indent = `-${Spacing16}`, ...restProps }: INavigationButton) {
    const slider = useScrollSnapSliderContext();
    const { showPrev, showNext } = useToggleNavigationButtons(slider);

    return (
        <StyledButton
            $show={(direction === 'prev' && showPrev) || (direction === 'next' && showNext)}
            $indent={indent}
            direction={direction}
            tabIndex={-1}
            aria-hidden
            {...restProps}
        />
    );
}
